import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
  Unlink,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';
import { getSupabaseAdminClient, Profile, PartnerConnection } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

type ConnectionStatus = 'all' | 'active' | 'pending' | 'removed';

/**
 * Obergrenze der Profil-Treffer, ueber die gesucht wird.
 *
 * Die gefundenen IDs stehen zweimal im Query-String (einmal je
 * Teilnehmer-Spalte), rund 37 Byte je UUID - bei 150 also gut 11 KB. Das
 * passt noch in die ueblichen Header-Puffer, mehr waere ein Risiko. Wird die
 * Grenze erreicht, sagt die Oberflaeche das, statt Treffer stillschweigend zu
 * schlucken; unbegrenzt ginge es erst mit einer Suche in der Datenbank (RPC).
 */
const SEARCH_PROFILE_LIMIT = 150;

/**
 * Wert fuer einen PostgREST-Filter in Anfuehrungszeichen setzen. Ohne das
 * zerlegt ein Komma im Suchbegriff ("Doe, John") die or()-Liste in zwei
 * Bedingungen.
 */
const quoteFilterValue = (value: string) =>
  `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

interface PartnerConnectionWithUsers extends PartnerConnection {
  user_1: Pick<Profile, 'id' | 'email' | 'display_name'>;
  user_2: Pick<Profile, 'id' | 'email' | 'display_name'> | null;
}

export function PartnersPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  // Der entprellte Begriff geht an die Datenbank. Seit die Suche dort laeuft,
  // kostet jeder Tastendruck sonst zwei Abfragen.
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConnectionStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const trimmed = searchQuery.trim();
    const timer = setTimeout(() => {
      setSearchTerm((previous) => (previous === trimmed ? previous : trimmed));
      // Ein neuer Begriff hat eine eigene Trefferzahl - Seite 3 von vorher
      // waere sonst leer.
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Die Kennzahlen zeigen den Gesamtbestand und haengen deshalb bewusst weder
  // an der Seitenauswahl noch am Statusfilter: frueher wurden sie aus der
  // geladenen Seite gezaehlt und sprangen beim Blaettern. Gezaehlt wird in der
  // Datenbank (head: true laedt keine Zeilen, nur den Count).
  const { data: counts } = useQuery({
    queryKey: ['partner-connection-counts'],
    queryFn: async () => {
      const countFor = async (status?: string) => {
        let query = supabase
          .from('partner_connections')
          .select('id', { count: 'exact', head: true });

        if (status) query = query.eq('status', status);

        const { count, error } = await query;
        if (error) throw error;
        return count || 0;
      };

      const [total, active, pending, removed] = await Promise.all([
        countFor(),
        countFor('active'),
        countFor('pending'),
        countFor('removed'),
      ]);

      return { total, active, pending, removed };
    },
  });

  // Fetch partner connections with pagination
  const { data: connectionsData, isLoading } = useQuery({
    queryKey: ['partner-connections', currentPage, searchTerm, statusFilter],
    queryFn: async () => {
      // Die Suche laeuft in zwei Schritten, weil PostgREST nicht in einem Zug
      // ueber beide eingebetteten Profile filtern kann: ein Filter auf einem
      // Embed wirkt immer nur auf dessen eigenen Join, ein ODER darueber
      // hinweg ist nicht ausdrueckbar. Also erst die passenden Profile holen,
      // dann die Verbindungen ueber deren IDs - dasselbe Muster wie in
      // UsersPage. Frueher wurde stattdessen die bereits geladene Seite
      // clientseitig gefiltert; damit blieb alles unauffindbar, was auf einer
      // anderen Seite lag, und die Seite zeigte weniger als pageSize Zeilen.
      let matchedProfileIds: string[] | null = null;
      let searchTruncated = false;

      if (searchTerm) {
        const pattern = quoteFilterValue(`%${searchTerm}%`);

        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id')
          .or(`email.ilike.${pattern},display_name.ilike.${pattern}`)
          // Ein Treffer mehr als erlaubt: nur so ist zu erkennen, dass die
          // Grenze ueberschritten wurde.
          .limit(SEARCH_PROFILE_LIMIT + 1);

        if (profilesError) throw profilesError;

        const ids = (profiles as { id: string }[]).map((profile) => profile.id);
        searchTruncated = ids.length > SEARCH_PROFILE_LIMIT;
        matchedProfileIds = ids.slice(0, SEARCH_PROFILE_LIMIT);

        // Kein passendes Profil - dann gibt es auch keine Verbindung, und die
        // zweite Abfrage kann entfallen.
        if (matchedProfileIds.length === 0) {
          return { connections: [], totalCount: 0, searchTruncated: false };
        }
      }

      let query = supabase
        .from('partner_connections')
        .select(`
          *,
          user_1:profiles!partner_connections_user_1_id_fkey(id, email, display_name),
          user_2:profiles!partner_connections_user_2_id_fkey(id, email, display_name)
        `, { count: 'exact' });

      // Apply status filter
      if (statusFilter === 'active') {
        query = query.eq('status', 'active');
      } else if (statusFilter === 'pending') {
        query = query.eq('status', 'pending');
      } else if (statusFilter === 'removed') {
        query = query.eq('status', 'removed');
      }

      // Apply search filter
      if (matchedProfileIds) {
        const idList = matchedProfileIds.join(',');
        query = query.or(`user_1_id.in.(${idList}),user_2_id.in.(${idList})`);
      }

      // Apply pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        connections: data as PartnerConnectionWithUsers[],
        // Zaehlt jetzt die Treffer der Suche, nicht mehr den Gesamtbestand -
        // die Pagination passt damit zum tatsaechlich gefilterten Ergebnis.
        totalCount: count || 0,
        searchTruncated,
      };
    },
  });

  const totalCount = connectionsData?.totalCount ?? 0;
  // Mindestens eine Seite: bei null Treffern waere totalPages sonst 0, der
  // "weiter"-Knopf aktiv und die naechste Abfrage ginge auf Seite 0 - also
  // auf einen negativen range(). Seit die Suche serverseitig filtert, ist ein
  // leeres Ergebnis der Normalfall, nicht die Ausnahme.
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Trennen heisst wie in der user-app: Status auf 'removed' setzen, nicht
  // loeschen - die Verbindung bleibt als Historie erhalten.
  const disconnectMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase
        .from('partner_connections')
        .update({ status: 'removed' } as never)
        .eq('id', connectionId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Verbindung gelöst');
      queryClient.invalidateQueries({ queryKey: ['partner-connections'] });
      queryClient.invalidateQueries({ queryKey: ['partner-connection-counts'] });
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const handleDisconnect = (connection: PartnerConnectionWithUsers) => {
    const names = [
      connection.user_1?.display_name || connection.user_1?.email || 'Unbenannt',
      connection.user_2?.display_name || connection.user_2?.email || 'ausstehende Einladung',
    ].join(' – ');

    if (confirm(`Verbindung wirklich lösen?\n\n${names}`)) {
      disconnectMutation.mutate(connection.id);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatRelativeDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) return 'gerade eben';
    if (hours < 24) return `vor ${hours}h`;
    if (days === 1) return 'gestern';
    if (days < 7) return `vor ${days} Tagen`;
    return formatDate(dateString);
  };

  // Nur das Icon, ohne Hintergrund - dafuer groesser. Die Farbe allein darf die
  // Bedeutung nicht tragen, deshalb steht der Status als Titel und
  // Screenreader-Text am umschliessenden Element (lucide-Icons nehmen kein
  // title-Prop entgegen).
  const getStatusBadge = (status: string) => {
    const badge = {
      active: { Icon: CheckCircle, color: 'text-emerald-600', label: 'Aktiv' },
      pending: { Icon: Clock, color: 'text-amber-600', label: 'Ausstehend' },
      removed: { Icon: XCircle, color: 'text-slate-500', label: 'Entfernt' },
    }[status];

    if (!badge) {
      return <span className="text-xs text-slate-600">{status}</span>;
    }

    const { Icon, color, label } = badge;

    return (
      <span className="inline-flex" role="img" title={label} aria-label={label}>
        <Icon className={`w-5 h-5 ${color}`} aria-hidden="true" />
      </span>
    );
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Absenden soll nicht auf die Entprellung warten muessen.
    setSearchTerm(searchQuery.trim());
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Partner-Verbindungen</h1>
        <p className="text-slate-600">Übersicht aller Partner-Verknüpfungen</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{counts?.total ?? 0}</div>
              <div className="text-sm text-slate-600">Gesamt</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{counts?.active ?? 0}</div>
              <div className="text-sm text-slate-600">Aktiv</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{counts?.pending ?? 0}</div>
              <div className="text-sm text-slate-600">Ausstehend</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Unlink className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{counts?.removed ?? 0}</div>
              <div className="text-sm text-slate-600">Entfernt</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <form onSubmit={handleSearch} className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nach E-Mail oder Name suchen..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
            </div>
            {connectionsData?.searchTruncated && (
              <p className="mt-2 text-xs text-amber-700">
                Mehr als {SEARCH_PROFILE_LIMIT} passende Profile – es werden nur die
                Verbindungen der ersten {SEARCH_PROFILE_LIMIT} gezeigt. Suchbegriff
                eingrenzen.
              </p>
            )}
          </form>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as ConnectionStatus);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="pending">Ausstehend</option>
              <option value="removed">Entfernt</option>
            </select>
          </div>
        </div>
      </div>

      {/* Connections Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">User 1</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">User 2</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Erstellt am</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Verbunden seit</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Lädt...
                  </td>
                </tr>
              ) : connectionsData?.connections?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Keine Partner-Verbindungen gefunden
                  </td>
                </tr>
              ) : (
                connectionsData?.connections?.map((connection) => (
                  <tr key={connection.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {connection.user_1?.display_name || 'Unbenannt'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {connection.user_1?.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {connection.user_2 ? (
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {connection.user_2.display_name || 'Unbenannt'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {connection.user_2.email}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400 italic">
                          Einladung ausstehend
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(connection.status)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">
                        {formatDate(connection.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">
                        {formatRelativeDate(connection.connected_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDisconnect(connection)}
                          disabled={
                            connection.status === 'removed' || disconnectMutation.isPending
                          }
                          className="p-1 hover:bg-slate-100 rounded disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          title="Verbindung lösen"
                        >
                          <Unlink className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {totalCount === 0
              ? 'Keine Treffer'
              : `Zeige ${((currentPage - 1) * pageSize) + 1}-${Math.min(currentPage * pageSize, totalCount)} von ${totalCount.toLocaleString('de-DE')}`}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page: number;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 border border-slate-300 rounded ${
                    currentPage === page
                      ? 'bg-teal-500 text-white border-teal-500'
                      : 'hover:bg-slate-100'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="px-2 py-1">...</span>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100"
                >
                  {totalPages}
                </button>
              </>
            )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
